import { useState } from 'react'
import { PrismicRichText } from '@prismicio/react'
import type { RichTextField } from '@prismicio/client'
import type { CollectionProductSample } from '@lib/types'

import styles from '../styles/CustomizationModal.module.scss'

type CustomizationReviewCardProps = {
	product: CollectionProductSample
	variant: CollectionProductSample['sampleVariants'][0]
	onEdit?: () => void
	/** Optional tag text to display in the top left */
	tagText?: string
	/** Tag icon type: 'none' | 'plus' | 'info'. Defaults to 'none'. */
	tagIcon?: 'none' | 'plus' | 'info'
	/** Optional tooltip text for info icon (Prismic rich text) */
	tooltipText?: RichTextField
}

export default function CustomizationReviewCard({
	product,
	variant,
	onEdit,
	tagText,
	tagIcon = 'none',
	tooltipText
}: CustomizationReviewCardProps) {
	const [showTooltip, setShowTooltip] = useState(false)
	return (
		<li className="customization-card w-auto text-left">
			<div
				className={`${styles.cardContent} grid grid-cols-[1.5fr,2.5fr] items-center bg-white shadow-bbx-card mb-[12px] relative`}
			>
				<div className="text-center relative w-full aspect-[1] bg-contain bg-center bg-no-repeat h-full max-w-[176px] max-h-fit">
					{/* Image */}
					{variant.image ? (
						<img
							src={`${variant.image.url}&width=400`}
							alt={variant.image.altText || 'Product image'}
							// removed w-full as some images were overflowing in height
							className="w-full object-contain max-h-full"
						/>
					) : (
						product.images.map((image, idx) => (
							// eslint-disable-next-line react/no-array-index-key
							<img src={image.url} alt="" key={idx} className="w-full h-full object-contain" />
						))
					)}
					{/* Tag overlay after image for proper overlap */}
					{tagText && (
						<div
							style={{ position: 'absolute', top: 0, left: 0, width: '100%' }}
							className="flex items-center justify-center px-3 py-1 bg-bbx-glimmer z-20 relative"
							data-testid="customization-tag"
						>
							{tagIcon === 'plus' && (
								<span className="mr-2">
									<img src="/images/plus.svg" alt="plus icon" width={16} height={16} />
								</span>
							)}
							<span className="text-white text-sm font-medium">{tagText}</span>
							{tagIcon === 'info' && tooltipText && (
								<span
									className="ml-2 cursor-pointer relative"
									onMouseEnter={() => setShowTooltip(true)}
									onMouseLeave={() => setShowTooltip(false)}
									role="button"
									tabIndex={0}
									aria-label="More information"
								>
									<i className="fa-solid fa-circle-info text-white text-base" />
									{showTooltip && (
										<div
											className="absolute left-1/2 -translate-x-1/2 top-full mt-2 bg-white text-gray-800 p-3 rounded-lg shadow-lg z-30 w-64 text-left"
											style={{ minWidth: '256px' }}
										>
											<div className="text-sm">
												<PrismicRichText field={tooltipText} />
											</div>
											{/* Tooltip arrow */}
											<div
												className="absolute bottom-full left-1/2 -translate-x-1/2 w-0 h-0"
												style={{
													borderLeft: '8px solid transparent',
													borderRight: '8px solid transparent',
													borderBottom: '8px solid white'
												}}
											/>
										</div>
									)}
								</span>
							)}
						</div>
					)}
				</div>
				<div className="py-[24px] px-[24px]">
					<h4 className="font-bold">{product.vendor}</h4>
					<p className="mb-[8px]">{variant.title}</p>

					{/* This is used if a product is out of stock */}
					{/* <div className="rounded-md bg-bbx-spark/10 flex p-4 w-full max-w-[800px]">
            <i className="fa-solid fa-bell-exclamation flex-none text-bbx-spark pl-1 pr-4 pt-1" />
            <p className="">
              <strong className="font-medium">Out of stock</strong>
              <br />
              <button type="button" className="text-bbx-spark hover:underline" onClick={onEdit}>
                Select another item
              </button>
            </p>
          </div> */}

					{/* Button should be hidden if out of stock */}
					{onEdit ? (
						<button type="button" className="text-bbx-spark hover:underline" onClick={onEdit}>
							Edit
						</button>
					) : null}
				</div>
			</div>
		</li>
	)
}
